import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { PriorityBadge, TicketIdBadge, STATUS_LABEL } from "@/components/StatusBadge";
import SLABadge from "@/components/SLABadge";
import TaskTypeBadge, { TASK_TYPE_OPTIONS, TASK_TYPE_META } from "@/components/TaskTypeBadge";
import { KanbanSquare, RefreshCcw } from "lucide-react";

const COLUMNS = [
  { id: "new",              label: "New",              tone: "border-red-500/40 bg-red-500/5" },
  { id: "assigned",         label: "Assigned",         tone: "border-indigo-500/40 bg-indigo-500/5" },
  { id: "in_progress",      label: "In Progress",      tone: "border-amber-500/40 bg-amber-500/5" },
  { id: "pending_customer", label: "Pending Customer", tone: "border-red-500/40 bg-red-500/5" },
  { id: "resolved",         label: "Resolved",         tone: "border-emerald-500/40 bg-emerald-500/5" },
];

export default function Board() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragOver, setDragOver] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [ticketType, setTicketType] = useState("all");
  const [taskType, setTaskType] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (ticketType !== "all") params.ticket_type = ticketType;
      if (taskType !== "all") params.internal_task_type = taskType;
      const { data } = await api.get("/tickets", { params });
      setTickets(data.filter((t) => t.status !== "closed" && t.status !== "cancelled"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load board");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [ticketType, taskType]);

  const grouped = useMemo(() => {
    const g = Object.fromEntries(COLUMNS.map((c) => [c.id, []]));
    for (const t of tickets) {
      const col = g[t.status] ? t.status : null;
      if (col) g[col].push(t);
      else if (t.status === "open" || t.status === "reopened") g["assigned"].push(t);
      else if (t.status === "pending_third_party") g["in_progress"].push(t);
    }
    return g;
  }, [tickets]);

  const move = async (ticketId, newStatus) => {
    const t = tickets.find((x) => x.ticket_id === ticketId);
    if (!t || t.status === newStatus) return;
    // Optimistic update
    setTickets((prev) => prev.map((x) => (x.ticket_id === ticketId ? { ...x, status: newStatus } : x)));
    try {
      await api.patch(`/tickets/${ticketId}`, { status: newStatus });
      toast.success(`${t.ticket_number} → ${STATUS_LABEL[newStatus] || newStatus}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Move failed");
      await load(); // rollback via reload
    }
  };

  const onDragStart = (e, t) => {
    setDraggingId(t.ticket_id);
    e.dataTransfer.setData("text/plain", t.ticket_id);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragEnd = () => { setDraggingId(null); setDragOver(null); };
  const onDragOverCol = (e, colId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOver !== colId) setDragOver(colId);
  };
  const onDropCol = async (e, colId) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain");
    setDragOver(null);
    setDraggingId(null);
    if (id) await move(id, colId);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-red-600 inline-flex items-center gap-2">
            <KanbanSquare className="h-3 w-3" strokeWidth={1.5} /> Workflow
          </div>
          <h1 className="font-display text-4xl font-black tracking-tight mt-2" data-testid="board-heading">Board</h1>
          <p className="text-sm text-slate-500 mt-2">Drag any ticket card between columns to change its status. Changes save instantly. Type <code className="text-red-600">@name</code> in a comment to loop in a colleague.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 border border-slate-200 rounded-sm p-0.5 mr-2" data-testid="board-type-tabs">
            {[
              { id: "all", label: "All" },
              { id: "customer", label: "Customer" },
              { id: "internal", label: "Internal" },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => { setTicketType(t.id); if (t.id !== "internal") setTaskType("all"); }}
                data-testid={`board-type-tab-${t.id}`}
                className={`px-3 h-8 text-xs font-mono uppercase tracking-[0.15em] rounded-sm transition-colors ${
                  ticketType === t.id ? "bg-red-600 text-white" : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <Link to="/tickets" className="text-xs font-mono uppercase tracking-[0.2em] text-slate-600 hover:text-slate-900" data-testid="board-to-list">List view</Link>
          <button
            onClick={load}
            className="border border-slate-200 bg-transparent hover:bg-slate-100 rounded-sm px-3 h-9 text-xs inline-flex items-center gap-2"
            data-testid="board-refresh"
          >
            <RefreshCcw className="h-3 w-3" strokeWidth={1.5} /> Refresh
          </button>
        </div>
      </div>

      {/* Task-type chip row (only when Internal tab is active) */}
      {ticketType === "internal" && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="board-task-type-chips">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Task type:</span>
          <button
            onClick={() => setTaskType("all")}
            data-testid="board-task-type-chip-all"
            className={`px-3 h-7 text-[11px] font-mono uppercase tracking-[0.15em] rounded-sm border transition-colors ${
              taskType === "all" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
            }`}
          >All</button>
          {TASK_TYPE_OPTIONS.map((tt) => {
            const active = taskType === tt;
            const meta = TASK_TYPE_META[tt];
            const Icon = meta.icon;
            return (
              <button
                key={tt}
                onClick={() => setTaskType(tt)}
                data-testid={`board-task-type-chip-${tt}`}
                className={`inline-flex items-center gap-1.5 px-3 h-7 text-[11px] font-mono uppercase tracking-[0.15em] rounded-sm border transition-colors ${
                  active ? "bg-red-600 text-white border-red-600" : `${meta.tone} hover:brightness-95`
                }`}
              >
                <Icon className="h-3 w-3" strokeWidth={1.75} />
                {meta.label}
              </button>
            );
          })}
        </div>
      )}

      {loading ? (
        <div className="p-10 text-center text-xs font-mono uppercase tracking-[0.3em] text-slate-500" data-testid="board-loading">Loading board…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="board-columns">
          {COLUMNS.map((col) => {
            const items = grouped[col.id] || [];
            const isOver = dragOver === col.id;
            return (
              <div
                key={col.id}
                data-testid={`board-col-${col.id}`}
                onDragOver={(e) => onDragOverCol(e, col.id)}
                onDrop={(e) => onDropCol(e, col.id)}
                onDragLeave={() => setDragOver((v) => (v === col.id ? null : v))}
                className={`border ${col.tone} rounded-sm p-3 min-h-[300px] transition-colors ${isOver ? "ring-2 ring-cyan-400/60" : ""}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-700">{col.label}</div>
                  <div className="text-[10px] font-mono text-slate-500">{items.length}</div>
                </div>
                <div className="space-y-2 min-h-[40px]">
                  {items.length === 0 ? (
                    <div className="text-[11px] text-slate-600 italic px-2 py-3">Drop a card here…</div>
                  ) : items.map((t) => (
                    <Link
                      key={t.ticket_id}
                      to={`/tickets/${t.ticket_id}`}
                      draggable
                      onDragStart={(e) => onDragStart(e, t)}
                      onDragEnd={onDragEnd}
                      data-testid={`board-card-${t.ticket_number}`}
                      className={`block bg-white border border-slate-200 rounded-sm p-3 hover:border-red-500/50 cursor-grab active:cursor-grabbing transition-all ${draggingId === t.ticket_id ? "opacity-40" : ""}`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <TicketIdBadge id={t.ticket_number} />
                        <PriorityBadge priority={t.priority} />
                      </div>
                      {t.ticket_type === "internal" && t.internal_task_type && (
                        <div className="mb-1.5"><TaskTypeBadge type={t.internal_task_type} size="sm" /></div>
                      )}
                      <div className="text-sm text-slate-900 leading-snug line-clamp-2 mb-1.5">{t.title}</div>
                      <div className="text-[10px] font-mono text-slate-500 truncate">
                        {t.assignee_name || "Unassigned"}{t.reporter_name ? ` · ${t.reporter_name}` : ""}
                      </div>
                      <div className="mt-2">
                        <SLABadge
                          dueAt={t.resolution_due_at}
                          status={t.status}
                          responseDueAt={t.first_response_due_at}
                          firstResponseAt={t.first_response_at}
                          compact
                        />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
